import { type SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SubjectResponse } from '@/@types/client-api';
import Modal, { ModalProps } from '@/components/ui/Modal/Modal';
import AddLicenseModal from '@/components/ui/LicenseModals/AddLicenseModal';
import PracticeAreaModal from '@/components/ui/SearchModals/PracticeAreaModal';
import { useGetLicenseData, useOnSaveLicense } from '@/components/ui/LicensesPage/hooks';
import { SelectorItem } from '@/components/ui/Filter/types';
import useToast from '@/hooks/useToast';
import useDebounceLoading from '@/hooks/useDebounceLoading';
import userPracticeAreasEndpoints, {
  useFollowPracticeAreasMutation,
  useGetUserPracticeAreasQuery
} from '@/redux/api/client/practiceAreas';
import taxonomyEndpoints, { useGetTaxonomySubjectsQuery } from '@/redux/api/client/taxonomy';
import {
  findCreditJurisdiction,
  isLicenseExcludedFromCreditPlans,
  LicenseData
} from '@/components/ui/SearchModals/utils/licenses';
import { getFollowingPracticeAreasMessage } from '@/components/ui/SearchModals/utils/practice-areas';
import { useGetExcludedLicensesQuery } from '@/redux/api/client/creditPlan';
import {
  CREDIT_PLANS_PRACTICEAREA_COOLDOWN_COOKIE_NAME,
  CreditPlansSparseDataFlowSteps
} from './util/constants';
import { SparseDataModalContent } from '@/@types/content';
import { AnalyticsContext } from '@/analytics/constants';
import { setCookieValues } from '@/utils/cookie-list-values';
import ComplianceModal, {
  type ComplianceModalProps
} from '@/components/ui/CreditTracker/Modals/ComplianceModal';
import { type ComplianceFormFields } from '@/components/ui/CreditTracker/Forms/ComplianceForm';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ScreenSizeQueries } from '@/constants/breakpoints';
import { buildCreditPlansCookieName, shouldSkipPracticeAreaSteps } from './util/CreditPlanHelpers';
import { useAuth } from 'react-oidc-context';

export type CreditPlansSparseDataModalContent = Partial<
  Record<
    CreditPlansSparseDataFlowSteps,
    SparseDataModalContent & {
      hiddenLicenseErrorMessage?: string;
    }
  >
>;

export type CreditPlansSparseDataFlowData = {
  license?: LicenseData;
  practiceAreas: SubjectResponse[];
  subjects: SubjectResponse[];
  skippedPracticeAreas: boolean;
  skippedSubjects: boolean;
};

export type CreditPlansSparseDataFlowProps = ModalProps & {
  stepOrder?: CreditPlansSparseDataFlowSteps[];
  saveLicenseOnSubmit?: boolean;
  content?: CreditPlansSparseDataModalContent;
  complianceModalProps?: Omit<ComplianceModalProps, 'open' | 'setOpen'> & {
    shouldShow?: boolean;
  };
  practiceAreaModalCooldown?: number;
  onDataChange?: (data: CreditPlansSparseDataFlowData) => void;
  onComplete?: (data: CreditPlansSparseDataFlowData) => void | Promise<void>;
};

const defaultStepOrder = [
  CreditPlansSparseDataFlowSteps.addLicense,
  CreditPlansSparseDataFlowSteps.addComplianceInformation,
  CreditPlansSparseDataFlowSteps.addPracticeAreas,
  CreditPlansSparseDataFlowSteps.addSpecificPracticeAreas
];
const practiceAreaSteps = [
  CreditPlansSparseDataFlowSteps.addPracticeAreas,
  CreditPlansSparseDataFlowSteps.addSpecificPracticeAreas
];

const orderForTwoColumnRows = <T,>(items: T[]) => [
  ...items.filter((_, index) => index % 2 === 0),
  ...items.filter((_, index) => index % 2 === 1)
];

const emptyFlowData: CreditPlansSparseDataFlowData = {
  practiceAreas: [],
  subjects: [],
  skippedPracticeAreas: false,
  skippedSubjects: false
};

const getSubjectCode = (subject: SubjectResponse) => subject.code ?? '';
const normalizeSubjectCode = (code: string) => code.replace('subject-', '');
const getSubjectCodeVariants = (code: string) =>
  code.startsWith('subject-') ? [code, normalizeSubjectCode(code)] : [code, `subject-${code}`];

/**
 * Modal flow for collecting the missing user data needed before generating a Credit Plan.
 *
 * @remarks
 * Steps are rendered in `stepOrder` using {@link CreditPlansSparseDataFlowSteps}. The flow can
 * add a license, follow parent practice areas, optionally follow specific child practice areas,
 * then call `onComplete` so the parent can wait for provider refresh before closing.
 *
 * @param props - See {@link CreditPlansSparseDataFlowProps}.
 */
const CreditPlansSparseDataFlow = ({
  open,
  setOpen,
  content,
  stepOrder = defaultStepOrder,
  saveLicenseOnSubmit = true,
  complianceModalProps,
  practiceAreaModalCooldown,
  onDataChange,
  onComplete
}: CreditPlansSparseDataFlowProps) => {
  const auth = useAuth();
  const indivSk = (auth.user?.profile?.IndividualSK ?? auth.user?.profile?.individualSk) as string;

  const [stepIndex, setStepIndex] = useState(0);
  const [flowData, setFlowData] = useState<CreditPlansSparseDataFlowData>(emptyFlowData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [licenseFooterErrorMessage, setLicenseFooterErrorMessage] = useState<string>();
  const skipPracticeAreaSteps = shouldSkipPracticeAreaSteps(indivSk);
  const usesTwoColumnGrid = useMediaQuery(ScreenSizeQueries.sm);
  const isSubmittingRef = useRef(false);

  const { showSuccessMessage, showFailMessage } = useToast();
  const [followPracticeAreas] = useFollowPracticeAreasMutation();
  const saveLicense = useOnSaveLicense({
    shouldRefetch: false,
    context: AnalyticsContext.Homepage
  });

  const {
    enrichedData,
    availableCreditJurisdictions,
    creditTypeOptions,
    jurisdictionOptions,
    complianceGroupOptions,
    licenseNumberSettings,
    resetLicenseData,
    useOnDataChange: handleLicenseFormDataChange
  } = useGetLicenseData(!open);
  const { data: excludedLicenseKeys } = useGetExcludedLicensesQuery(undefined, {
    skip: !open
  });

  const orderedSteps = useMemo(() => {
    const steps = stepOrder.length ? stepOrder : defaultStepOrder;
    return skipPracticeAreaSteps ? steps.filter(step => !practiceAreaSteps.includes(step)) : steps;
  }, [skipPracticeAreaSteps, stepOrder]);
  const currentStep = orderedSteps[Math.min(stepIndex, orderedSteps.length - 1)];
  const hasNextStep = stepIndex < orderedSteps.length - 1;
  const showBackButton = stepIndex > 0;
  const shouldShowComplianceStep =
    !!complianceModalProps && complianceModalProps.shouldShow !== false;

  // #region Practice Areas

  const practiceAreaCooldownSelectionCookieName = useMemo(
    () => buildCreditPlansCookieName(CREDIT_PLANS_PRACTICEAREA_COOLDOWN_COOKIE_NAME, indivSk),
    [indivSk]
  );

  /** Prefetch the practice areas in the background. */
  const taxonomyQueryArgs = useMemo(() => ({ onlyBabyPool: false, excludeEmpty: true }), []);

  const shouldUsePracticeAreas = practiceAreaSteps.includes(currentStep);
  const prefetchSubjects = taxonomyEndpoints.usePrefetch('getTaxonomySubjects');
  const prefetchUserPracticeAreas = userPracticeAreasEndpoints.usePrefetch('getUserPracticeAreas');
  useEffect(() => {
    if (open && shouldUsePracticeAreas) {
      prefetchSubjects(taxonomyQueryArgs);
      prefetchUserPracticeAreas();
    }
  }, [
    open,
    prefetchSubjects,
    prefetchUserPracticeAreas,
    shouldUsePracticeAreas,
    taxonomyQueryArgs
  ]);

  const {
    data: practiceAreas,
    isError: isPracticeAreasError,
    isLoading: isPracticeAreasLoading
  } = useGetTaxonomySubjectsQuery(taxonomyQueryArgs, {
    skip: !open || !shouldUsePracticeAreas
  });

  const {
    data: userPracticeAreasData,
    isError: isUserPracticeAreasError,
    isLoading: isUserPracticeAreasLoading
  } = useGetUserPracticeAreasQuery(undefined, {
    skip: !open || !shouldUsePracticeAreas
  });

  //#endregion

  const isPracticeAreaDataPending = !isPracticeAreasError && !practiceAreas;
  const isUserPracticeAreasDataPending = !isUserPracticeAreasError && !userPracticeAreasData;

  const followedPracticeAreaCodeSet = useMemo(
    () =>
      new Set(
        userPracticeAreasData?.items
          ?.filter(a => !!a.id)
          .flatMap(a => getSubjectCodeVariants(a.id!)) ?? []
      ),
    [userPracticeAreasData?.items]
  );

  const selectedPracticeAreaCodeSet = useMemo(() => {
    const all = flowData.practiceAreas.map(getSubjectCode);
    return new Set([...all, ...followedPracticeAreaCodeSet]);
  }, [flowData.practiceAreas, followedPracticeAreaCodeSet]);

  const selectedSubjectCodeSet = useMemo(
    () => new Set([...flowData.subjects.map(getSubjectCode), ...followedPracticeAreaCodeSet]),
    [flowData.subjects, followedPracticeAreaCodeSet]
  );

  const practiceAreaByCode = useMemo(
    () => new Map(practiceAreas?.map(practiceArea => [getSubjectCode(practiceArea), practiceArea])),
    [practiceAreas]
  );

  const practiceAreaItems = useMemo((): SelectorItem[] => {
    const items = (practiceAreas ?? [])
      .filter(practiceArea => !!practiceArea.code)
      .map(practiceArea => ({
        checked: selectedPracticeAreaCodeSet.has(getSubjectCode(practiceArea)),
        label: practiceArea.name ?? '',
        value: getSubjectCode(practiceArea)
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return usesTwoColumnGrid ? orderForTwoColumnRows(items) : items;
  }, [practiceAreas, selectedPracticeAreaCodeSet, usesTwoColumnGrid]);

  const selectedPracticeAreas = useMemo(
    () =>
      (practiceAreas ?? []).filter(practiceArea =>
        selectedPracticeAreaCodeSet.has(getSubjectCode(practiceArea))
      ),
    [practiceAreas, selectedPracticeAreaCodeSet]
  );

  const selectedPracticeAreasWithSubjects = useMemo(
    () =>
      (practiceAreas ?? []).filter(
        practiceArea =>
          practiceArea.subjects?.some(subject => !!subject.code) &&
          (selectedPracticeAreaCodeSet.has(getSubjectCode(practiceArea)) ||
            practiceArea.subjects.some(subject =>
              selectedSubjectCodeSet.has(getSubjectCode(subject))
            ))
      ),
    [practiceAreas, selectedPracticeAreaCodeSet, selectedSubjectCodeSet]
  );

  const selectedSubjects = useMemo(() => {
    const result = new Map<string, SubjectResponse>();
    selectedPracticeAreasWithSubjects.forEach(practiceArea => {
      practiceArea.subjects?.forEach(subject => {
        const code = getSubjectCode(subject);
        if (selectedSubjectCodeSet.has(code)) {
          result.set(code, subject);
        }
      });
    });
    return [...result.values()];
  }, [selectedPracticeAreasWithSubjects, selectedSubjectCodeSet]);

  const subjectByCode = useMemo(() => {
    const result = new Map<string, SubjectResponse>();
    selectedPracticeAreasWithSubjects.forEach(practiceArea => {
      practiceArea.subjects?.forEach(subject => {
        const code = getSubjectCode(subject);
        if (code && !result.has(code)) {
          result.set(code, subject);
        }
      });
    });
    return result;
  }, [selectedPracticeAreasWithSubjects]);

  const subjectItems = useMemo((): SelectorItem[] => {
    const usedCodes = new Set<string>();
    return [...selectedPracticeAreasWithSubjects]
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
      .flatMap((practiceArea, sectionOrder) => {
        const items = (practiceArea.subjects ?? [])
          .filter(subject => {
            const code = getSubjectCode(subject);
            if (!code || usedCodes.has(code)) return false;
            usedCodes.add(code);
            return true;
          })
          .map(subject => {
            const code = getSubjectCode(subject);
            return {
              checked: selectedSubjectCodeSet.has(code),
              label: subject.name ?? '',
              sectionId: getSubjectCode(practiceArea),
              sectionOrder,
              sectionTitle: practiceArea.name ?? '',
              value: code
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label));

        return usesTwoColumnGrid ? orderForTwoColumnRows(items) : items;
      });
  }, [selectedPracticeAreasWithSubjects, selectedSubjectCodeSet, usesTwoColumnGrid]);

  const debouncedPracticeAreasLoading = useDebounceLoading(
    isPracticeAreasLoading || isUserPracticeAreasLoading
  );

  const updateFlowData = useCallback(
    (
      getNextData: (currentData: CreditPlansSparseDataFlowData) => CreditPlansSparseDataFlowData
    ) => {
      setFlowData(currentData => {
        const nextData = getNextData(currentData);
        onDataChange?.(nextData);
        return nextData;
      });
    },
    [onDataChange]
  );

  const submitFlowData = useCallback(
    async (data: CreditPlansSparseDataFlowData) => {
      if (isSubmittingRef.current) {
        return;
      }

      const practiceAreaCodes = data.skippedPracticeAreas
        ? []
        : data.practiceAreas.map(getSubjectCode);
      const subjectCodes = data.skippedSubjects ? [] : data.subjects.map(getSubjectCode);
      const codesToFollow = [...new Set([...practiceAreaCodes, ...subjectCodes])]
        .filter(Boolean)
        .map(normalizeSubjectCode)
        .filter(code => !followedPracticeAreaCodeSet.has(code));
      const codesToFollowSet = new Set(codesToFollow);

      isSubmittingRef.current = true;
      setIsSubmitting(true);
      try {
        const licenseSaved = saveLicenseOnSubmit
          ? await saveLicense(data.license ?? enrichedData)
          : true;
        if (!licenseSaved) {
          return;
        }

        if (codesToFollow.length) {
          try {
            await followPracticeAreas(codesToFollow).unwrap();
            showSuccessMessage(
              getFollowingPracticeAreasMessage(
                [...data.practiceAreas, ...data.subjects]
                  .filter(subject =>
                    codesToFollowSet.has(normalizeSubjectCode(getSubjectCode(subject)))
                  )
                  .map(subject => subject.name ?? '')
              )
            );
          } catch (error) {
            showFailMessage('Something went wrong');
          }
        }

        if (onComplete) {
          await onComplete(data);
        } else {
          setOpen(false);
        }
      } finally {
        setIsSubmitting(false);
        isSubmittingRef.current = false;
      }
    },
    [
      enrichedData,
      followedPracticeAreaCodeSet,
      followPracticeAreas,
      onComplete,
      saveLicense,
      saveLicenseOnSubmit,
      setOpen,
      showFailMessage,
      showSuccessMessage
    ]
  );

  const advanceOrSubmit = useCallback(
    (data: CreditPlansSparseDataFlowData) => {
      if (isSubmittingRef.current) {
        return;
      }

      updateFlowData(() => data);
      if (hasNextStep) {
        setStepIndex(prev => prev + 1);
        return;
      }
      submitFlowData(data);
    },
    [hasNextStep, submitFlowData, updateFlowData]
  );

  const handleBack = useCallback(() => {
    if (isSubmittingRef.current) {
      return;
    }

    setStepIndex(prev => Math.max(prev - 1, 0));
  }, []);

  const handleLicenseDataChange = useCallback(
    (data: LicenseData) => {
      handleLicenseFormDataChange(data);
      const selectedCreditJurisdiction = findCreditJurisdiction(
        availableCreditJurisdictions,
        data.creditClass_SK,
        data.creditRegion_SK
      );
      const hiddenLicenseErrorMessage =
        content?.[CreditPlansSparseDataFlowSteps.addLicense]?.hiddenLicenseErrorMessage;

      setLicenseFooterErrorMessage(
        isLicenseExcludedFromCreditPlans(data, excludedLicenseKeys, selectedCreditJurisdiction)
          ? hiddenLicenseErrorMessage
          : undefined
      );
      updateFlowData(currentData => ({ ...currentData, license: data }));
    },
    [
      availableCreditJurisdictions,
      content,
      excludedLicenseKeys,
      handleLicenseFormDataChange,
      updateFlowData
    ]
  );

  const handleLicenseSave = useCallback(
    (licenseData?: LicenseData) => {
      const currentLicense = (licenseData ?? flowData.license ?? enrichedData) as
        LicenseData | undefined;
      const creditJurisdiction = findCreditJurisdiction(
        availableCreditJurisdictions,
        currentLicense?.creditClass_SK,
        currentLicense?.creditRegion_SK
      );
      const license = {
        ...currentLicense,
        complianceGroups: creditJurisdiction?.complianceGroups ?? currentLicense?.complianceGroups,
        creditRegion: creditJurisdiction?.id ?? currentLicense?.creditRegion,
        state: creditJurisdiction?.stateName ?? currentLicense?.state,
        stateDescription: creditJurisdiction?.name ?? currentLicense?.stateDescription
      };

      const hiddenLicenseErrorMessage =
        content?.[CreditPlansSparseDataFlowSteps.addLicense]?.hiddenLicenseErrorMessage;

      if (isLicenseExcludedFromCreditPlans(license, excludedLicenseKeys, creditJurisdiction)) {
        setLicenseFooterErrorMessage(hiddenLicenseErrorMessage);
        return;
      }
      advanceOrSubmit({
        ...flowData,
        license
      });
    },
    [
      advanceOrSubmit,
      availableCreditJurisdictions,
      content,
      enrichedData,
      excludedLicenseKeys,
      flowData
    ]
  );

  const handleComplianceComplete = useCallback(
    (compliancePeriod?: ComplianceFormFields) => {
      complianceModalProps?.onComplete?.(compliancePeriod);
      advanceOrSubmit(flowData);
    },
    [advanceOrSubmit, complianceModalProps, flowData]
  );

  const handleComplianceSecondaryButtonPress = useCallback(() => {
    complianceModalProps?.onSecondaryButtonPress?.();
    advanceOrSubmit(flowData);
  }, [advanceOrSubmit, complianceModalProps, flowData]);

  const setComplianceModalOpen = useCallback(
    (nextOpen: SetStateAction<boolean>) => {
      if (typeof nextOpen === 'function') {
        setOpen(open => {
          const resolvedOpen = nextOpen(open);
          return !resolvedOpen && hasNextStep ? open : resolvedOpen;
        });
        return;
      }

      if (!nextOpen && hasNextStep) {
        return;
      }
      setOpen(nextOpen);
    },
    [hasNextStep, setOpen]
  );

  const handlePracticeAreaValueChanged = useCallback(
    (value?: string, checked?: boolean) => {
      const practiceArea = value ? practiceAreaByCode.get(value) : undefined;
      if (!practiceArea) {
        return;
      }

      updateFlowData(currentData => {
        const isChecked = checked ?? !currentData.practiceAreas.some(item => item.code === value);
        const practiceAreas = isChecked
          ? [...currentData.practiceAreas.filter(item => item.code !== value), practiceArea]
          : currentData.practiceAreas.filter(item => item.code !== value);
        const subjects = currentData.subjects.filter(subject =>
          practiceAreas.some(practiceArea =>
            practiceArea.subjects?.some(child => child.code === subject.code)
          )
        );

        return {
          ...currentData,
          practiceAreas,
          skippedPracticeAreas: false,
          skippedSubjects: subjects.length ? currentData.skippedSubjects : false,
          subjects
        };
      });
    },
    [practiceAreaByCode, updateFlowData]
  );

  const handleSubjectValueChanged = useCallback(
    (value?: string, checked?: boolean) => {
      const subject = value ? subjectByCode.get(value) : undefined;
      if (!subject) {
        return;
      }

      updateFlowData(currentData => {
        const isChecked = checked ?? !currentData.subjects.some(item => item.code === value);
        return {
          ...currentData,
          skippedSubjects: false,
          subjects: isChecked
            ? [...currentData.subjects.filter(item => item.code !== value), subject]
            : currentData.subjects.filter(item => item.code !== value)
        };
      });
    },
    [subjectByCode, updateFlowData]
  );

  const handlePracticeAreaSave = useCallback(() => {
    const data = {
      ...flowData,
      practiceAreas: selectedPracticeAreas
    };

    if (
      !selectedPracticeAreasWithSubjects.length &&
      orderedSteps[stepIndex + 1] === CreditPlansSparseDataFlowSteps.addSpecificPracticeAreas
    ) {
      updateFlowData(() => data);
      if (stepIndex + 2 < orderedSteps.length) {
        setStepIndex(stepIndex + 2);
        return;
      }
      submitFlowData(data);
      return;
    }

    advanceOrSubmit(data);
  }, [
    advanceOrSubmit,
    flowData,
    orderedSteps,
    selectedPracticeAreas,
    selectedPracticeAreasWithSubjects.length,
    stepIndex,
    submitFlowData,
    updateFlowData
  ]);

  const handleSubjectSave = useCallback(() => {
    advanceOrSubmit({
      ...flowData,
      practiceAreas: selectedPracticeAreas,
      subjects: selectedSubjects
    });
  }, [advanceOrSubmit, flowData, selectedPracticeAreas, selectedSubjects]);

  const handlePracticeAreaSkip = useCallback(() => {
    const data = {
      ...flowData,
      practiceAreas: [],
      skippedPracticeAreas: true,
      skippedSubjects: true,
      subjects: []
    };

    if (!!practiceAreaModalCooldown && practiceAreaModalCooldown > 0)
      setCookieValues(
        practiceAreaCooldownSelectionCookieName,
        ['true'],
        1,
        practiceAreaModalCooldown
      );

    if (orderedSteps[stepIndex + 1] === CreditPlansSparseDataFlowSteps.addSpecificPracticeAreas) {
      updateFlowData(() => data);
      if (stepIndex + 2 < orderedSteps.length) {
        setStepIndex(stepIndex + 2);
        return;
      }
      submitFlowData(data);
      return;
    }

    advanceOrSubmit(data);
  }, [
    advanceOrSubmit,
    flowData,
    orderedSteps,
    practiceAreaCooldownSelectionCookieName,
    practiceAreaModalCooldown,
    stepIndex,
    submitFlowData,
    updateFlowData
  ]);

  const handleSubjectSkip = useCallback(() => {
    advanceOrSubmit({
      ...flowData,
      practiceAreas: selectedPracticeAreas,
      skippedSubjects: true,
      subjects: []
    });

    if (!!practiceAreaModalCooldown && practiceAreaModalCooldown > 0)
      setCookieValues(
        practiceAreaCooldownSelectionCookieName,
        ['true'],
        1,
        practiceAreaModalCooldown
      );
  }, [
    advanceOrSubmit,
    flowData,
    practiceAreaCooldownSelectionCookieName,
    practiceAreaModalCooldown,
    selectedPracticeAreas
  ]);

  const handlePracticeAreasUnavailable = useCallback(() => {
    const data = {
      ...flowData,
      practiceAreas: [],
      skippedPracticeAreas: true,
      skippedSubjects: true,
      subjects: []
    };
    const nextStepIndex = orderedSteps.findIndex(
      (step, index) => index > stepIndex && !practiceAreaSteps.includes(step)
    );

    updateFlowData(() => data);
    if (nextStepIndex > -1) {
      setStepIndex(nextStepIndex);
      return;
    }
    submitFlowData(data);
  }, [flowData, orderedSteps, stepIndex, submitFlowData, updateFlowData]);

  useEffect(() => {
    if (
      !open ||
      currentStep !== CreditPlansSparseDataFlowSteps.addComplianceInformation ||
      shouldShowComplianceStep
    ) {
      return;
    }

    advanceOrSubmit(flowData);
  }, [advanceOrSubmit, currentStep, flowData, open, shouldShowComplianceStep]);

  useEffect(() => {
    if (
      !open ||
      !practiceAreaSteps.includes(currentStep) ||
      isPracticeAreasLoading ||
      isUserPracticeAreasLoading ||
      isPracticeAreaDataPending ||
      isUserPracticeAreasDataPending ||
      (!isPracticeAreasError &&
        !isUserPracticeAreasError &&
        (currentStep !== CreditPlansSparseDataFlowSteps.addPracticeAreas ||
          practiceAreaItems.length))
    ) {
      return;
    }

    handlePracticeAreasUnavailable();
  }, [
    currentStep,
    handlePracticeAreasUnavailable,
    isPracticeAreaDataPending,
    isPracticeAreasError,
    isPracticeAreasLoading,
    isUserPracticeAreasDataPending,
    isUserPracticeAreasError,
    isUserPracticeAreasLoading,
    open,
    practiceAreaItems.length
  ]);

  useEffect(() => {
    if (
      !open ||
      currentStep !== CreditPlansSparseDataFlowSteps.addSpecificPracticeAreas ||
      isPracticeAreasLoading ||
      isUserPracticeAreasLoading ||
      subjectItems.length
    ) {
      return;
    }

    advanceOrSubmit({
      ...flowData,
      skippedSubjects: true,
      subjects: []
    });
  }, [
    advanceOrSubmit,
    currentStep,
    flowData,
    isPracticeAreasLoading,
    isUserPracticeAreasLoading,
    open,
    subjectItems.length
  ]);

  useEffect(() => {
    if (!open || !skipPracticeAreaSteps || orderedSteps.length) {
      return;
    }

    submitFlowData({
      ...emptyFlowData,
      skippedPracticeAreas: true,
      skippedSubjects: true
    });
  }, [open, orderedSteps.length, skipPracticeAreaSteps, submitFlowData]);

  useEffect(() => {
    if (open) {
      setStepIndex(0);
      setFlowData(emptyFlowData);
      setLicenseFooterErrorMessage(undefined);
      setIsSubmitting(false);
      setLicenseFooterErrorMessage(undefined);
      isSubmittingRef.current = false;
      onDataChange?.(emptyFlowData);
      resetLicenseData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stepOrder]);

  const component = useMemo(() => {
    if (currentStep === CreditPlansSparseDataFlowSteps.addLicense) {
      const _content = content?.[CreditPlansSparseDataFlowSteps.addLicense];
      return (
        <AddLicenseModal
          open={open}
          setOpen={setOpen}
          data={enrichedData}
          onDataChange={handleLicenseDataChange}
          creditTypeOptions={creditTypeOptions}
          jurisdictionOptions={jurisdictionOptions}
          complianceGroupOptions={complianceGroupOptions}
          onSave={handleLicenseSave}
          licenseNumberSettings={licenseNumberSettings}
          isSubmitLoading={isSubmitting}
          saveButtonDisabled={!!licenseFooterErrorMessage}
          footerErrorMessage={licenseFooterErrorMessage}
          onBack={showBackButton ? handleBack : undefined}
          renderContentOnly
          content={{
            title: _content?.heading || 'What CLE jurisdiction do you need credits for?',
            subheading: _content?.subHeading,
            saveButtonLabel: _content?.primaryButtonText || 'Save'
          }}
          shouldCloseOnSave={false}
        />
      );
    }

    if (
      currentStep === CreditPlansSparseDataFlowSteps.addComplianceInformation &&
      shouldShowComplianceStep
    ) {
      const { shouldShow: _, ...modalProps } = complianceModalProps;
      return (
        <ComplianceModal
          {...modalProps}
          open={open}
          setOpen={setComplianceModalOpen}
          onComplete={handleComplianceComplete}
          onSecondaryButtonPress={handleComplianceSecondaryButtonPress}
        />
      );
    }

    if (!skipPracticeAreaSteps) {
      if (currentStep === CreditPlansSparseDataFlowSteps.addSpecificPracticeAreas) {
        const _content = content?.[CreditPlansSparseDataFlowSteps.addSpecificPracticeAreas];
        return (
          <PracticeAreaModal
            items={subjectItems}
            onSave={handleSubjectSave}
            onDismissButtonPress={handleSubjectSkip}
            onValueChanged={handleSubjectValueChanged}
            isLoading={debouncedPracticeAreasLoading}
            content={{
              title: _content?.heading || 'Select specific practice areas',
              subheading: _content?.subHeading,
              saveButtonLabel: _content?.primaryButtonText || 'Save and generate plan',
              dismissButtonLabel: _content?.secondaryButtonText || 'Skip'
            }}
            shouldDismissButtonClose={false}
            onBack={handleBack}
            allowSections
            isSubmitLoading={isSubmitting}
          />
        );
      }

      const _content = content?.[CreditPlansSparseDataFlowSteps.addPracticeAreas];
      return (
        <PracticeAreaModal
          items={practiceAreaItems}
          onSave={handlePracticeAreaSave}
          onDismissButtonPress={handlePracticeAreaSkip}
          onValueChanged={handlePracticeAreaValueChanged}
          isLoading={debouncedPracticeAreasLoading}
          content={{
            title: _content?.heading || 'What are your practice areas?',
            subheading: _content?.subHeading,
            saveButtonLabel: _content?.primaryButtonText || 'Save',
            dismissButtonLabel: _content?.secondaryButtonText || 'Skip'
          }}
          shouldDismissButtonClose={false}
          onBack={showBackButton ? handleBack : undefined}
          isSubmitLoading={isSubmitting}
        />
      );
    }
  }, [
    complianceGroupOptions,
    complianceModalProps,
    content,
    creditTypeOptions,
    currentStep,
    debouncedPracticeAreasLoading,
    enrichedData,
    handleBack,
    handleLicenseDataChange,
    handleLicenseSave,
    handleComplianceComplete,
    handleComplianceSecondaryButtonPress,
    handlePracticeAreaSave,
    handlePracticeAreaSkip,
    handlePracticeAreaValueChanged,
    handleSubjectSave,
    handleSubjectSkip,
    handleSubjectValueChanged,
    isSubmitting,
    jurisdictionOptions,
    licenseNumberSettings,
    licenseFooterErrorMessage,
    open,
    practiceAreaItems,
    setComplianceModalOpen,
    setOpen,
    showBackButton,
    shouldShowComplianceStep,
    skipPracticeAreaSteps,
    subjectItems
  ]);

  if (
    currentStep === CreditPlansSparseDataFlowSteps.addComplianceInformation &&
    shouldShowComplianceStep
  ) {
    return component;
  }

  return (
    <Modal open={open} setOpen={setOpen}>
      {component}
    </Modal>
  );
};

export default CreditPlansSparseDataFlow;
